import axios from 'axios';
import { getUserRole } from '@/utils/auth';
import { storeUserProfileFromApi } from '@/utils/sessionAuth';
import {
  hasVerifiedProfile,
  verifiedHasAnyPermission,
  verifiedHasPermission,
  isVerifiedAdmin,
  getVerifiedRole,
  getVerifiedPermissions,
  getVerifiedRoleEmploye,
} from '@/utils/verifiedProfile';

export const PERMISSIONS = Object.freeze({
  VOIR: 'voir',
  LECTURE_SEULE_GLOBAL: 'lecture_seule_global',
  VALIDER: 'valider',
  CONFORMITE: 'conformite',
  GESTION_UTILISATEURS: 'gestion_utilisateurs',
  IT: 'it',
  TECHNICIEN: 'technicien',
});

/** Décisions d'accès : profil vérifié serveur uniquement. */
function requireVerified() {
  return hasVerifiedProfile();
}

export function getUserPermissions() {
  if (!requireVerified()) return [];
  return getVerifiedPermissions();
}

export function hasPermission(permission) {
  if (!requireVerified()) return false;
  return verifiedHasPermission(permission);
}

export function hasAnyPermission(required) {
  if (!requireVerified()) return false;
  return verifiedHasAnyPermission(required);
}

export function canReadBackoffice() {
  return hasAnyPermission([
    PERMISSIONS.VOIR,
    PERMISSIONS.LECTURE_SEULE_GLOBAL,
    PERMISSIONS.VALIDER,
    PERMISSIONS.CONFORMITE,
    PERMISSIONS.GESTION_UTILISATEURS,
  ]);
}

export function canEditBackoffice() {
  return hasAnyPermission([
    PERMISSIONS.VALIDER,
    PERMISSIONS.CONFORMITE,
    PERMISSIONS.GESTION_UTILISATEURS,
  ]);
}

export function canSendAccessCode() {
  return hasAnyPermission([PERMISSIONS.CONFORMITE, PERMISSIONS.GESTION_UTILISATEURS]);
}

export function canManageRecommandationIA() {
  if (!requireVerified()) return false;
  if (isVerifiedAdmin()) return true;
  const role = getVerifiedRole();
  const job = getVerifiedRoleEmploye();
  if (role === 'saisie' || role === 'validation' || job === 'saisie' || job === 'validation') {
    return canReadBackoffice();
  }
  return canEditBackoffice();
}

export function canPublishRecommandationIA() {
  if (!requireVerified()) return false;
  if (isVerifiedAdmin()) return true;
  const role = getVerifiedRole();
  const job = getVerifiedRoleEmploye();
  if (role === 'validation' || job === 'validation') {
    return canEditBackoffice();
  }
  return hasAnyPermission([
    PERMISSIONS.VALIDER,
    PERMISSIONS.CONFORMITE,
    PERMISSIONS.GESTION_UTILISATEURS,
  ]);
}

export function canManageContactMessageStatut() {
  return canEditBackoffice();
}

export function canSeeContactEmployeColumn() {
  return isVerifiedAdmin() || hasPermission(PERMISSIONS.GESTION_UTILISATEURS);
}

function staffFamilyRole() {
  return String(getVerifiedRole() || getUserRole() || '').trim().toLowerCase();
}

function staffJob() {
  return String(getVerifiedRoleEmploye() || '').trim().toLowerCase();
}

/** Création / modification / suppression des recommandations mandat. */
export function canEditMandat() {
  if (!requireVerified()) return false;
  if (isVerifiedAdmin()) return true;
  const role = staffFamilyRole();
  const job = staffJob();
  return role === 'validation' || job === 'validation' || job === 'admin';
}

/** Lecteur mandat (saisie) : pas de création, formulaire en lecture seule. */
export function isMandatViewer() {
  if (!requireVerified()) return false;
  if (canEditMandat()) return false;
  const role = staffFamilyRole();
  const job = staffJob();
  return role === 'saisie' || job === 'saisie';
}

/** Validation de statut (saisie + éditeurs). */
export function canValidateMandat() {
  return canEditMandat() || isMandatViewer();
}

/** Onglet « Modèles PDF » (zones de signature) — réservé IT / technicien uniquement (pas conformité). */
export function canManageSignaturePdfTemplates() {
  if (!requireVerified()) return false;
  const perms = getVerifiedPermissions().map((p) => String(p).toLowerCase());
  if (perms.includes(PERMISSIONS.IT) || perms.includes(PERMISSIONS.TECHNICIEN)) return true;
  const roleEmploye = getVerifiedRoleEmploye();
  return roleEmploye === 'it' || roleEmploye === 'technicien';
}

/** Page « Diagnostic IT » — réservée IT / technicien (permissions OU role_employe), pas de bypass admin. */
export function canAccessItDiagnostic() {
  if (!requireVerified()) return false;
  const perms = getVerifiedPermissions().map((p) => String(p).toLowerCase());
  if (perms.includes(PERMISSIONS.IT) || perms.includes(PERMISSIONS.TECHNICIEN)) return true;
  const roleEmploye = getVerifiedRoleEmploye();
  return roleEmploye === 'it' || roleEmploye === 'technicien';
}

/** True si le compte n'a QUE l'habilitation IT (aucune permission backoffice large) —
 * sert à le cantonner à la page diagnostic dans le router guard. */
export function isItOnlyStaff() {
  if (!canAccessItDiagnostic()) return false;
  if (isVerifiedAdmin()) return false;
  return !canReadBackoffice();
}

/** Affichage UI (libellés) — peut utiliser le rôle issu du dernier profil serveur. */
export function getDisplayRole() {
  return getVerifiedRole() || getUserRole() || '';
}

export async function syncEmployeProfileFromApi() {
  try {
    const { data } = await axios.get('/api/profile/', { withCredentials: true });
    storeUserProfileFromApi(data);
  } catch (_) {
    /* session ou réseau */
  }
}
